// Favourites.js
import React, { useState, useEffect } from 'react';
import ListingCard from './ListingCard';
import { db, auth } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  arrayRemove,
  arrayUnion,
  collection,
  query,
  where,
  documentId,
  getDocs,
} from 'firebase/firestore';
import './Favourites.css';

import { getLocationDisplay, cities, regions } from '../constants/locations';
import { getAccommodationTypeWithTenants } from '../constants/accommodationTypes';
import { getAmenitiesAr, getAmenitiesEn, getAmenitiesValues } from '../constants/amenities';
import { getTimeAgo } from '../constants/timeUtils';

/**
 * دالة transformAdData للإعلانات العادية (العقارات)
 */
const transformAdData = (id, data) => {
  return {
    id,
    userId: data.userId,
    image: data.ad_images?.[0],
    images: data.ad_images || [],
    title: data.accommodation_type,
    location: {
      ar: getLocationDisplay(data.city, data.region, 'ar'),
      en: getLocationDisplay(data.city, data.region, 'en'),
      city: data.city,
      region: data.region
    },
    price: data.price,
    type: {
      ar: getAccommodationTypeWithTenants(data.accommodation_type, data.tenants_policy, 'ar'),
      en: getAccommodationTypeWithTenants(data.accommodation_type, data.tenants_policy, 'en'),
      value: data.accommodation_type
    },
    phoneNumber: data.ad_contact_phone,
    whatsappNumber: data.ad_contact_whatsapp,
    amenities: {
      ar: getAmenitiesAr(data.amenities),
      en: getAmenitiesEn(data.amenities),
      values: getAmenitiesValues(data.amenities)
    },
    description: data.ad_description,
    timeSincePosted: {
      ar: getTimeAgo(data.createdAt, 'ar'),
      en: getTimeAgo(data.createdAt, 'en')
    },
    createdAtTimestamp: data.createdAt?.toDate?.() || new Date(data.createdAt),
    tenants: data.tenants_policy,
    visitorsPolicy: data.visitors_policy,
    petsPolicy: data.pets_policy,
    priceDuration: data.price_type,
    smokingPolicy: data.smoking_policy,
    insurance: data.insurance,
    priceIncludesBills: data.price_includes_bills || false,
    latitude: data.latitude,
    longitude: data.longitude,
    status: data.status,
    adType: 'regular'
  };
};

/**
 * دالة transformProfileAdData لإعلانات الباحثين عن سكن (Profiles)
 */
const transformProfileAdData = (id, data) => {
  return {
    id,
    userId: data.userId,
    image: data.ad_images?.[0],
    images: data.ad_images || [],
    name: data.ad_contact_name,
    gender: data.gender,
    accommodationTypes: data.accommodation_type || [],
    budgetMin: data.budget_min,
    budgetMax: data.budget_max,
    priceDuration: data.price_type,
    location: {
      ar: getLocationDisplay(data.city, data.region, 'ar'),
      en: getLocationDisplay(data.city, data.region, 'en'),
      city: data.city,
      region: data.region
    },
    phoneNumber: data.ad_contact_phone,
    whatsappNumber: data.ad_contact_whatsapp,
    description: data.ad_description,
    timeSincePosted: {
      ar: getTimeAgo(data.createdAt, 'ar'),
      en: getTimeAgo(data.createdAt, 'en')
    },
    createdAtTimestamp: data.createdAt?.toDate?.() || new Date(data.createdAt),
    occupation: data.occupation,
    age: data.age,
    moveInDate: data.move_in_date,
    status: data.status,
    adType: 'profile'
  };
};

/**
 * دالة تنسيق موقع متعدد المناطق (نفس اللي في ListingsGrid.js)
 */
const formatMultiLocation = (city, regionsArray, language) => {
  if (!Array.isArray(regionsArray) || regionsArray.length === 0) {
    return getLocationDisplay(city, regionsArray, language);
  }

  const cityObj = cities.find(c => c.value === city);
  const cityText = language === 'ar'
    ? (cityObj?.ar || city)
    : (cityObj?.en || city);

  const regionTexts = regionsArray.map(regionValue => {
    const regionObj = regions[city]?.find(r => r.value === regionValue);
    return language === 'ar'
      ? (regionObj?.ar || regionValue)
      : (regionObj?.en || regionValue);
  });

  const separator = language === 'ar' ? '، ' : ', ';

  return `${cityText} - ${regionTexts.join(separator)}`;
};

/**
 * دالة transformTeamUpData لإعلانات تكوين فريق (Team Up)
 */
const transformTeamUpData = (id, data) => {
  const hasFlat = data.has_flat === 'yes';

  const location = hasFlat
    ? {
        ar: getLocationDisplay(data.city, data.region, 'ar'),
        en: getLocationDisplay(data.city, data.region, 'en'),
        city: data.city,
        region: data.region,
      }
    : {
        ar: Array.isArray(data.preferred_regions)
          ? formatMultiLocation(data.preferred_city, data.preferred_regions, 'ar')
          : getLocationDisplay(data.preferred_city, data.preferred_regions, 'ar'),
        en: Array.isArray(data.preferred_regions)
          ? formatMultiLocation(data.preferred_city, data.preferred_regions, 'en')
          : getLocationDisplay(data.preferred_city, data.preferred_regions, 'en'),
        city: data.preferred_city,
        region: data.preferred_regions,
      };

  return {
    id,
    userId: data.userId,
    image: data.profile_photo,
    personalName: data.personal_name,
    profilePhoto: data.profile_photo,
    gender: data.gender,
    age: data.age,
    desiredTeamSize: data.desired_team_size,
    currentTeamSize: data.current_team_size,
    hasFlat: data.has_flat,
    location,
    price: hasFlat ? data.monthly_rent : data.budget_per_person,
    priceDuration: 'monthly',
    insurance: hasFlat ? data.refundable_insurance : null,
    moveInDate: hasFlat ? data.move_in_date : data.preferred_move_in_date,
    description: hasFlat ? data.flat_description : data.looking_for_description,
    lifestyle: data.lifestyle || [],
    phoneNumber: data.contact_phone,
    whatsappNumber: data.contact_whatsapp,
    timeSincePosted: {
      ar: getTimeAgo(data.createdAt, 'ar'),
      en: getTimeAgo(data.createdAt, 'en'),
    },
    createdAtTimestamp: data.createdAt?.toDate?.() || new Date(data.createdAt),
    latitude: data.latitude,
    longitude: data.longitude,
    status: data.status,
    adType: 'team_up',
  };
};

// Firestore `in` operator supports at most 30 values.
const ID_CHUNK_SIZE = 30;

/**
 * Fetch listings by ID from ads / profile_ads / team_up_ads using batched
 * documentId() `in` queries. Only documents that actually exist are billed
 * as reads (unlike sequential getDoc which charges for missing docs too).
 */
const fetchListingsByIds = async (ids) => {
  if (!ids || ids.length === 0) return [];

  const byId = new Map();

  for (let i = 0; i < ids.length; i += ID_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + ID_CHUNK_SIZE);

    const [adsSnap, profileSnap, teamUpSnap] = await Promise.all([
      getDocs(query(collection(db, 'ads'), where(documentId(), 'in', chunk))),
      getDocs(query(collection(db, 'profile_ads'), where(documentId(), 'in', chunk))),
      getDocs(query(collection(db, 'team_up_ads'), where(documentId(), 'in', chunk))),
    ]);

    adsSnap.forEach((d) => {
      byId.set(d.id, transformAdData(d.id, d.data()));
    });
    profileSnap.forEach((d) => {
      if (!byId.has(d.id)) {
        byId.set(d.id, transformProfileAdData(d.id, d.data()));
      }
    });
    teamUpSnap.forEach((d) => {
      if (!byId.has(d.id)) {
        byId.set(d.id, transformTeamUpData(d.id, d.data()));
      }
    });
  }

  return ids.map((id) => byId.get(id)).filter(Boolean);
};

function Favourites({ currentLanguage, getLocalizedText, user, authLoading, isMobile }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favouriteIds, setFavouriteIds] = useState(new Set());

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setError(getLocalizedText('يجب تسجيل الدخول أولاً', 'Please login first'));
      setLoading(false);
      return;
    }

    const fetchFavourites = async () => {
      try {
        const userRef = doc(db, 'verified_users', user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const ids = userSnap.data().favourite_ads_id || [];
          setFavouriteIds(new Set(ids));

          if (ids.length === 0) {
            setListings([]);
            setLoading(false);
            return;
          }

          const adsData = await fetchListingsByIds(ids);

          adsData.sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp);
          setListings(adsData);
        } else {
          setError(getLocalizedText('لم يتم العثور على بيانات المستخدم', 'User data not found'));
        }
      } catch (err) {
        setError(getLocalizedText('خطأ في جلب الإعلانات المفضلة', 'Error fetching favourites'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFavourites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentLanguage, authLoading]);

  const toggleFavorite = async (listingId) => {
    if (!auth.currentUser) {
      alert(getLocalizedText('يجب تسجيل الدخول لإضافة إلى المفضلة', 'Please login to add to favourites'));
      return;
    }

    const isCurrentlyFavorited = favouriteIds.has(listingId);
    const userRef = doc(db, 'verified_users', auth.currentUser.uid);

    // تحديث تفاؤلي
    setFavouriteIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlyFavorited) next.delete(listingId);
      else next.add(listingId);
      return next;
    });

    // في صفحة المفضلة: لو شال من المفضلة نشيل الكارت من القائمة
    if (isCurrentlyFavorited) {
      setListings((prev) => prev.filter((l) => l.id !== listingId));
    }

    try {
      if (isCurrentlyFavorited) {
        await updateDoc(userRef, { favourite_ads_id: arrayRemove(listingId) });
      } else {
        await setDoc(userRef, { favourite_ads_id: arrayUnion(listingId) }, { merge: true });
      }
    } catch (err) {
      console.error('Error toggling favourite:', err);
      // rollback
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlyFavorited) next.add(listingId);
        else next.delete(listingId);
        return next;
      });
      alert(getLocalizedText('حدث خطأ، حاول مرة أخرى', 'Error, please try again'));
    }
  };

  const handleClose = () => {
    window.history.back();
  };

  if (authLoading || loading) {
    return (
      <div className="popup-page-overlay" onClick={handleClose}>
        <div className="popup-page-panel" onClick={(e) => e.stopPropagation()}>
          <div className="popup-page-header">
            <h2 className="popup-page-title">{getLocalizedText('المفضلة', 'Favourites')}</h2>
            <button type="button" className="popup-page-close-btn" onClick={handleClose} aria-label="close">
              ×
            </button>
          </div>
          <div className="popup-page-body">
            <div className="popup-page-loading">
              <div className="popup-page-spinner" />
              <p>{getLocalizedText(
                authLoading ? 'جاري التحقق من تسجيل الدخول...' : 'جاري التحميل...',
                authLoading ? 'Checking login status...' : 'Loading...'
              )}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="popup-page-overlay" onClick={handleClose}>
        <div className="popup-page-panel" onClick={(e) => e.stopPropagation()}>
          <div className="popup-page-header">
            <h2 className="popup-page-title">{getLocalizedText('المفضلة', 'Favourites')}</h2>
            <button type="button" className="popup-page-close-btn" onClick={handleClose} aria-label="close">
              ×
            </button>
          </div>
          <div className="popup-page-body">
            <p className="popup-page-empty">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="popup-page-overlay" onClick={handleClose}>
      <div className="popup-page-panel" onClick={(e) => e.stopPropagation()}>
        <div className="popup-page-header">
          <h2 className="popup-page-title">{getLocalizedText('المفضلة', 'Favourites')}</h2>
          <button type="button" className="popup-page-close-btn" onClick={handleClose} aria-label="close">
            ×
          </button>
        </div>

        <div className="popup-page-body">
          {listings.length > 0 ? (
            <div className={`popup-page-grid ${isMobile ? 'mobile-grid' : ''}`}>
              {listings.map(listing => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  currentLanguage={currentLanguage}
                  getLocalizedText={getLocalizedText}
                  favouriteIds={favouriteIds}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          ) : (
            <p className="popup-page-empty">
              {getLocalizedText('لا توجد إعلانات مفضلة بعد', 'No favourites yet')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Favourites;
